// Types voor module C3 Uren / Weekstaat. Volgt het patroon van verzuim.ts:
// string-literal unions die de Prisma-enums spiegelen, label-records voor UI,
// DTOs voor API-responses en Zod-schemas voor request-bodies.

import { z } from 'zod'
import { idParam } from './id-validators'
import type { DeclaratieType, DeclaratieStatus } from './declaraties'

// ─── Enums (matching Prisma enums as string unions) ──────────────────────────

export const WEEKSTAAT_STATUS = [
  'CONCEPT',
  'INGEDIEND',
  'GOEDGEKEURD',
  'AFGEKEURD',
  'INGETROKKEN',
  'DEFINITIEF',
] as const
export type WeekstaatStatus = (typeof WEEKSTAAT_STATUS)[number]

export const WEEKSTAAT_AUDIT_ACTIE = [
  'INGEDIEND',
  'GOEDGEKEURD',
  'AFGEKEURD',
  'INGETROKKEN',
  'TVT_OPBOUW_GETRIGGERD',
  'TVT_OPBOUW_TERUGGEDRAAID',
] as const
export type WeekstaatAuditActie = (typeof WEEKSTAAT_AUDIT_ACTIE)[number]

// ─── Label constants (NL-talig voor UI) ──────────────────────────────────────

export const WEEKSTAAT_STATUS_LABELS: Record<WeekstaatStatus, string> = {
  CONCEPT:     'Concept',
  INGEDIEND:   'Ingediend',
  GOEDGEKEURD: 'Goedgekeurd',
  AFGEKEURD:   'Afgekeurd',
  INGETROKKEN: 'Ingetrokken',
  DEFINITIEF:  'Definitief',
}

export const WEEKSTAAT_AUDIT_ACTIE_LABELS: Record<WeekstaatAuditActie, string> = {
  INGEDIEND:                'Ingediend',
  GOEDGEKEURD:              'Goedgekeurd',
  AFGEKEURD:                'Afgekeurd',
  INGETROKKEN:              'Ingetrokken',
  TVT_OPBOUW_GETRIGGERD:    'TVT-opbouw uitgevoerd',
  TVT_OPBOUW_TERUGGEDRAAID: 'TVT-opbouw teruggedraaid',
}

// Status badge variant voor StatusBadge-component.
export const WEEKSTAAT_STATUS_BADGE_VARIANT: Record<
  WeekstaatStatus,
  'info' | 'warning' | 'success' | 'danger' | 'secondary'
> = {
  CONCEPT:     'secondary', // grijs — nog niet ingediend
  INGEDIEND:   'warning',   // oranje — wacht op review
  GOEDGEKEURD: 'success',   // groen — goedgekeurd
  AFGEKEURD:   'danger',    // rood — moet aangepast
  INGETROKKEN: 'secondary', // grijs — niet meer relevant
  DEFINITIEF:  'info',      // blauw — automatisch afgesloten
}

// ─── DTOs voor API responses ─────────────────────────────────────────────────

/** Eén regel in de backoffice-lijst (één per week per medewerker). */
export interface WeekstaatListRowDTO {
  id: string
  employeeId: string
  employeeName: string
  employeeNumber: string
  year: number
  week: number
  weekStart: string // ISO yyyy-MM-dd (maandag)
  weekEnd: string // ISO yyyy-MM-dd (zondag)
  status: WeekstaatStatus
  totalUren: number
  totalOveruren: number
  totalReisKm: number
  totalReisMin: number
  /**
   * Week-net min-uren — alleen aanwezig wanneer de totalen via het week-net-pad
   * (DEFINITIEF) zijn berekend. Aanwezigheid schakelt `berekenUrenSplitsing`
   * naar de week-net-formule (ATV-band-fix, PO 2026-07-02); afwezig = legacy
   * per-dag-weergave (lopende/bevroren weken).
   */
  minuren?: number
  /**
   * FO-031 — ATV-opbouw-band in uren (38 -> 40 = 2) als de medewerker ATV
   * opbouwt, anders 0. Voedt samen met `totalUren`/`totalOveruren` de
   * `berekenUrenSplitsing` (Uren=basis, Overuren=ATV-bewust, ATV-opbouw los) —
   * DEZELFDE bron als de weekstaat-detail, geen tweede berekening.
   */
  atvBandUren: number
  /**
   * FO-031 — som van de VERLONING-toeslag-uren (ploeg/za/zo/feestdag) van de
   * week, uit dezelfde toeslag-engine als de weekstaat-detail (Posten-tab).
   */
  toeslagUren: number
  /**
   * FO-031 — naar TVT omgeboekte uren van de week (werk + reis), uit de
   * daadwerkelijk geboekte SaldoMutaties (WERKBON_OVERUREN/WERKBON_REISTIJD).
   * 0 zolang de week nog niet is afgesloten (er is dan nog niets omgeboekt).
   */
  tvtOmgeboektUren: number
  submittedAt: string | null // ISO timestamp
  beoordeeldOp: string | null // ISO datetime van approvedAt ?? rejectedAt
}

/** Verwijzing naar één werkbon op een weekstaat-dag (FO-028: klikbare links). */
export interface WeekstaatWerkbonRefDTO {
  id: string // werkbon-id (cuid) — link-target naar /werkbonnen/[id]
  number: string // human-readable werkbonNumber (bijv. WB2601444)
}

/**
 * Per-dag aggregatie binnen een weekstaat. Werkbon-dagen komen uit
 * WerkbonDayEntry; FO-028 voegt ook dagen toe die ALLEEN doorbetaalde
 * afwezigheid (verlof/ATV/ziekte/feestdag) hebben, zodat elke dag van de week
 * zichtbaar is en per dag te herleiden. Geen dubbeltelling: de weeknorm-telling
 * verandert niet, dit is per-dag-volledigheid (zie `WeekstaatVerrijkingDTO`).
 */
export interface WeekstaatDayDTO {
  date: string // ISO yyyy-MM-dd
  /** 1=ma .. 7=zo (ISO 8601), afgeleid uit de ECHTE datum — niet de rij-index. */
  dayOfWeek: number
  uren: number
  overuren: number
  reisKm: number
  reisMin: number
  werkbonNumbers: string[] // alle werkbonnen waar deze dag entries voor heeft
  /** FO-028 — werkbonnen als klikbare links (id + nummer); spiegelt `werkbonNumbers`. */
  werkbonRefs: WeekstaatWerkbonRefDTO[]
  /** Ziekte-uren voor deze dag uit VerzuimDag-ledger (read-only, nooit bewerkt). */
  ziekteUren?: number
  /**
   * #31a — goedgekeurd verlof (niet-ATV) als werkuren voor deze dag, op basis
   * van de dagwaarde (contracturen/werkdagen). Read-only; verlaagt elders de
   * weeknorm, telt hier NIET dubbel mee in `uren` (zie `WeekstaatVerrijkingDTO`).
   */
  verlofUren?: number
  /** #31a — goedgekeurd ATV-verlof als werkuren voor deze dag (read-only). */
  atvUren?: number
  /** FO-028 — feestdag-uren (dagwaarde) voor deze dag (read-only). */
  feestdagUren?: number
  /**
   * FO-473 — de facturabele uren van dezelfde werkbon-dagen (de factuur-as);
   * `uren` hierboven = de betaalde uren. Gelijk = geen verschil te tonen.
   */
  factuurUren?: number
  /** FO-473 — de pauze per dagregel die op deze dag start, met herkomst per as. */
  pauzeRegels?: WeekstaatPauzeRegelDTO[]
}

/** FO-473 — één dagregel-pauze in de weekstaat (backoffice, intern). */
export interface WeekstaatPauzeRegelDTO {
  werkbonNumber: string
  pauzeMinutes: number
  pauzeVerloning: 'GESCHREVEN' | 'DOORBETAALD'
  pauzeFacturatieMinuten: number | null
  pauzeBron: import('./pauze-tweeassig').PauzeBronKey | null
  pauzeVerloningBron: import('./pauze-tweeassig').PauzeBronKey | null
  pauzeFacturatieBron: import('./pauze-tweeassig').PauzeBronKey | null
  pauzeHandmatig: boolean
  pauzeHandmatigFacturatie: boolean
  betaaldeUren: number
  factuurUren: number
}

export interface WeekstaatAuditDTO {
  id: string
  actie: WeekstaatAuditActie
  actor: string
  actorName: string | null
  oudeStatus: WeekstaatStatus | null
  nieuweStatus: WeekstaatStatus | null
  /**
   * UI-driven uitbreiding: free-form change-payload als JSON-blob. Backend
   * mapt huidige status-transitions naar `{ status: <enum> }`. Toekomstige
   * audit-acties kunnen rijkere blobs schrijven zonder DTO-breaking change.
   */
  oudeWaarde: unknown | null
  nieuweWaarde: unknown | null
  toelichting: string | null
  createdAt: string
}

// ─── #31a — Week-overzicht verrijking (read-only, afgeleid op read) ───────────

/** Eén onbetaalde (goedgekeurde, nog-niet-uitbetaalde) declaratie in de week. */
export interface WeekstaatDeclaratieDTO {
  id: string
  datum: string // ISO yyyy-MM-dd
  type: DeclaratieType
  omschrijving: string
  bedrag: number
  status: DeclaratieStatus
}

/**
 * Eén VERLONING-toeslag-component die in de week van toepassing is, geaggregeerd
 * over de dag-entries. Geen bedrag: de toeslag-engine levert valueType + value
 * (zelfde bron als de payroll-herleiding), het bedrag rekent de payroll met het
 * persoonlijke uurloon — hier tonen we alleen welke toeslag op hoeveel uur valt.
 */
export interface WeekstaatToeslagComponentDTO {
  label: string | null
  dagType: string | null
  ploegSoort: string | null
  uren: number
  valueType: string
  value: number
}

/**
 * FO-352 (batch-40) §3.1 — toeslag-uren per soort in het cumulatief.
 *
 * Zelfde partitie als `ToeslagUrenDTO` (payroll): elke component landt in
 * precies één bucket, dus de som is sluitend en gelijk aan de toeslag-som van
 * het periode-overzicht (pariteitstest, AC1).
 */
export interface UrenCumulatiefToeslagDTO {
  tweePloegen: number
  driePloegen: number
  vijfPloegen: number
  zaterdag: number
  zondag: number
  feestdag: number
  overig: number
}

/**
 * Consignatie-/standby-samenvatting voor de week. `standbyVergoedingIndicatief`
 * is de som van de op de werkbon geconfigureerde vaste/ per-nacht-vergoeding;
 * `null` als geen bedrag geconfigureerd is. INDICATIEF: of dit een verlonings-
 * post of een facturatie-post is, wordt per BV/CAO bepaald (geescaleerd) — het
 * telt nergens automatisch mee in een uitbetaling.
 */
export interface WeekstaatConsignatieDTO {
  dagen: number
  werkbonNumbers: string[]
  reisKm: number
  reisMin: number
  standbyVergoedingIndicatief: number | null
}

/**
 * #31a — verrijking van het week-overzicht bovenop de bestaande uren-totalen.
 * Read-only, op read berekend uit dezelfde bronnen als de aggregatie (geen
 * dubbele opslag). De overuren/minuren komen week-net uit
 * `berekenWeekOverwerkEnMinuren`; `totaalInclusiefAfwezigheid` = werkuren +
 * meetellende doorbetaalde afwezigheid, gemeten tegen de VOLLE weeknorm, zodat
 * verlof/ziekte-als-werkuren niet dubbel telt met de norm-verlaging.
 */
export interface WeekstaatVerrijkingDTO {
  weeknorm: number
  werkuren: number
  reisUren: number
  overuren: number
  minuren: number
  verlofUren: number
  atvUren: number
  ziekteUren: number
  feestdagUren: number
  /** Som van de per BV-beleid MEETELLENDE doorbetaalde afwezige uren. */
  meetellendeAfwezigeUren: number
  /** werkuren + meetellendeAfwezigeUren (tegen de volle weeknorm gemeten). */
  totaalInclusiefAfwezigheid: number
  toeslagComponenten: WeekstaatToeslagComponentDTO[]
  declaraties: WeekstaatDeclaratieDTO[]
  declaratiesTotaal: number
  consignatie: WeekstaatConsignatieDTO | null
}

/**
 * UI-driven detail-shape voor de backoffice weekstaat-pagina. Velden zijn
 * NL-talig (employee-object, ingediendOp, beoordeeldOp, dagen) zodat de UI
 * direct binding doet zonder mapper. weekStart/weekEnd zijn ISO yyyy-MM-dd.
 */
export interface WeekstaatDetailDTO {
  id: string
  employeeId: string
  employee: {
    id: string
    firstName: string
    lastName: string
    employeeNumber: string
    functionTitle: string | null
  }
  year: number
  week: number
  weekStart: string
  weekEnd: string
  status: WeekstaatStatus
  totalUren: number
  totalOveruren: number
  totalReisKm: number
  totalReisMin: number
  /**
   * FO-031 — ATV-opbouw-band in uren (38 -> 40 = 2) als de medewerker ATV
   * opbouwt, anders 0. Met `totalUren`/`totalOveruren` voert `berekenUrenSplitsing`
   * de basis/ATV-opbouw/overuren-splitsing uit — DEZELFDE bron als de uren-lijst,
   * zodat de cijfers identiek zijn (geen tweede berekening).
   */
  atvBandUren: number
  ingediendOp: string | null
  approvedBy: string | null
  approvedAt: string | null
  rejectedBy: string | null
  rejectedAt: string | null
  beoordeeldOp: string | null
  beoordelingToelichting: string | null
  /**
   * Read-only preview van TVT-opbouw die zou plaatsvinden bij goedkeuring.
   * null als status al GOEDGEKEURD is (al getriggerd) of geen day-entries.
   *
   * FO-122 — `werkUren`/`reisUren` zijn de daadwerkelijke opbouw (na de
   * 50/100-factor); `*BronUren` is het bron-aantal (week-net overwerk resp.
   * uitbetaalde reisuren) en `*OpbouwPercentage` de configuratie, zodat de UI
   * "50% van X u = Y u" kan tonen. De opbouw spiegelt `triggerTVTFromWeekstaat`
   * exact (week-net, contract op de week-maandag, ATV-/TVT-gate + factor).
   */
  tvtImpact: {
    werkBronUren: number
    werkOpbouwPercentage: number
    werkUren: number
    reisBronUren: number
    reisOpbouwPercentage: number
    reisUren: number
  } | null
  dagen: WeekstaatDayDTO[]
  /** Totaal ziekte-uren voor de week uit VerzuimDag-ledger (read-only). */
  ziekteUrenTotaal?: number
  /**
   * #31a — verrijking (verlof/ziekte als werkuren, declaraties, consignatie,
   * toeslag-componenten, werkuren/reisuren/toeslagen/overuren-scheiding).
   * Read-only en op read berekend; optioneel zodat oudere lezers blijven werken.
   */
  verrijking?: WeekstaatVerrijkingDTO
  audits: WeekstaatAuditDTO[]
  createdAt: string
  updatedAt: string
}

/** Portal-overzicht: huidige week + samenvattingen van vorige 4 weken. */
export interface UrenWeekSummaryDTO {
  year: number
  week: number
  weekStart: string // ISO yyyy-MM-dd (maandag)
  weekEnd: string // ISO yyyy-MM-dd (zondag)
  weekstaatId: string | null
  status: WeekstaatStatus | null // null als er nog geen Weekstaat-record voor deze week is
  totalUren: number
  totalOveruren: number
  totalReisKm: number
  totalReisMin: number
}

/** Per-dag breakdown voor huidige week (mobile-first dag-cards ma-zo). */
export interface UrenDagDTO {
  date: string // ISO yyyy-MM-dd
  dayOfWeek: number // 1=ma ... 7=zo (ISO 8601)
  werkbonNumbers: string[] // werkbonNumber van werkbonnen die deze dag bevatten
  uren: number
  overuren: number
  reisKm: number
  reisMin: number
  /** Ziekte-uren voor deze dag uit VerzuimDag-ledger (read-only, nooit bewerkt). */
  ziekteUren?: number
  /** #31a — goedgekeurd verlof (niet-ATV) als werkuren voor deze dag (read-only). */
  verlofUren?: number
  /** #31a — goedgekeurd ATV-verlof als werkuren voor deze dag (read-only). */
  atvUren?: number
}

/** Top-level overview voor portal /uren — current week + 4 vorige. */
export interface UrenOverviewDTO {
  year: number
  week: number
  weekStart: string
  weekEnd: string
  /**
   * FO-031 — ATV-opbouw-band (38 -> 40 = 2) als de medewerker ATV opbouwt, anders
   * 0. Voedt samen met de gesommeerde dag-uren/-overuren `berekenUrenSplitsing`
   * (Uren = basis, ATV-opbouw los, Overuren), zodat de huidige-week-tegels niet
   * langer het totaal (incl. overuren) náást de overuren tonen — dat las dubbel.
   */
  atvBandUren: number
  weekstaat: {
    id: string
    status: WeekstaatStatus
    beoordelingToelichting: string | null
  } | null
  dagen: UrenDagDTO[] // 7 stuks (ma-zo), totalUren=0 als geen entries
  previousWeeks: UrenWeekSummaryDTO[] // 4 vorige, oudste eerst
  /** Totaal ziekte-uren voor de huidige week uit VerzuimDag-ledger (read-only). */
  ziekteUrenTotaal?: number
  /**
   * Betaalde-afwezigheid-totalen voor de huidige week (read-only), naast de
   * gewerkte uren getoond zodat de week compleet is. Verlof = goedgekeurd verlof
   * (niet-ATV); feestdag = doorbetaalde feestdag-uren. Optioneel (alleen > 0).
   */
  verlofUrenTotaal?: number
  feestdagUrenTotaal?: number
}

// ─── FO-115 (batch-22) — eigen uren over een periode (portal) ─────────────────
//
// De medewerker-app krijgt naast "deze week" ook een maand-/eigen-periode-filter.
// De splitsing basis/ATV-opbouw/overuren wordt per ISO-week berekend (CAO:
// overuren is week-net, niet per-dag opgeteld) — dus de cijfers blijven één
// waarheid met de weekstaat. Read-only; geen nieuwe brondata.
//
// FO-491 (batch-66) — de periode kent twee optel-modi:
//   KALENDER  geknipt op [van, totEnMet]: de dagenlijst én alle totalen tellen
//             alleen de dagen binnen de periode. Elke dag telt zo in precies één
//             maand; de som van alle maanden is het jaartotaal. Default.
//   WEKEN     hele ISO-weken (maandag van de week van `van` t/m zondag van de week
//             van `totEnMet`), zoals de backoffice-weekstaatlijst. Een week op de
//             maandgrens telt zo in twee maanden mee. (De verloning telt per
//             kalenderdag van de maand, niet per hele week.)

export const UREN_PERIODE_MODI = ['KALENDER', 'WEKEN'] as const
export type UrenPeriodeModus = (typeof UREN_PERIODE_MODI)[number]

/**
 * FO-031-totalen over een periode. Dezelfde kolommen als de weekstaat-lijst
 * (`WeekstaatListRowDTO`): werk/overuren/reis + toeslag-uren + naar-TVT-omgeboekt.
 * Bij modus KALENDER geknipt op de periode, bij WEKEN over hele ISO-weken.
 */
export interface UrenPeriodeTotalenDTO {
  totalUren: number
  /**
   * Uren boven de (effectieve) weeknorm die binnen de periode vallen. Per ISO-week
   * berekend; het deel dat op dagen buiten de periode valt telt niet mee.
   */
  totalOveruren: number
  totalReisKm: number
  totalReisMin: number
  /** Som van de VERLONING-toeslag-uren over de periode (FO-031). */
  toeslagUren: number
  /** Som van de naar-TVT-omgeboekte uren over de periode (werk + reis, FO-031). */
  tvtOmgeboektUren: number
  /**
   * Betaalde-afwezigheid over de periode (read-only), naast de gewerkte uren.
   * Verlof = goedgekeurd verlof (niet-ATV); feestdag = doorbetaalde feestdag-uren.
   */
  verlofUren: number
  feestdagUren: number
  /**
   * FO-491 — de splitsing van `totalUren`: per ISO-week berekend
   * (`berekenUrenSplitsing`) en daarna per dag toegerekend, zodat alleen de
   * dagen binnen de periode meetellen. De UI toont deze drie, niet een eigen
   * herberekening. De drie tellen op tot `totalUren`; één randgeval neemt de
   * verdeling ongewijzigd over van de week-splitsing: is de norm van een week
   * kleiner dan de ATV-band, dan telt die splitsing de band dubbel (bestond al
   * in de weekweergave) en is de som iets hoger.
   */
  basisUren: number
  atvOpbouwUren: number
  overuren: number
}

/**
 * Eén ISO-week die de periode raakt. De uren-/reis-/toeslag-/TVT-velden zijn het
 * AANDEEL van die week binnen de periode: bij modus WEKEN de hele week, bij
 * KALENDER alleen de dagen op of na `van` en op of voor `totEnMet`.
 */
export interface UrenPeriodeWeekDTO {
  year: number
  week: number
  weekStart: string // ISO yyyy-MM-dd (maandag)
  weekEnd: string // ISO yyyy-MM-dd (zondag)
  weekstaatId: string | null
  status: WeekstaatStatus | null
  totalUren: number
  totalOveruren: number
  totalReisKm: number
  totalReisMin: number
  toeslagUren: number
  tvtOmgeboektUren: number
  /**
   * FO-031 — ATV-opbouw-band van de HELE week (2 bij ATV-opbouw, anders 0). De
   * band is per ISO-week, niet per periode.
   */
  atvBandUren: number
  /**
   * Week-net min-uren van de HELE week — alleen aanwezig op het week-net-pad
   * (DEFINITIEF). Niet geknipt: de weeknorm geldt voor de week als geheel.
   */
  minuren?: number
  /**
   * FO-491 — basis/ATV-opbouw/overuren van deze week binnen de periode (tellen
   * op tot `totalUren`, op het randgeval bij de periode-totalen na). Per
   * ISO-week berekend (CAO) en chronologisch over de dagen verdeeld: de eerste
   * uren van de week vullen de basis, daarna de ATV-band, het laatste stuk is
   * overuren.
   */
  basisUren: number
  atvOpbouwUren: number
  overuren: number
  /** FO-491 — het deel van de week dat binnen de periode valt (ISO yyyy-MM-dd). */
  periodeStart: string
  periodeEind: string
  /**
   * FO-491 — aantal kalenderdagen van deze week vóór `van` resp. ná `totEnMet`.
   * Beide 0 = de week valt helemaal binnen de periode (altijd zo bij modus WEKEN).
   */
  dagenVoorPeriode: number
  dagenNaPeriode: number
}

/** Eén dag met activiteit binnen de periode (mobile dag-card). */
export interface UrenPeriodeDagDTO {
  date: string // ISO yyyy-MM-dd
  dayOfWeek: number // 1=ma .. 7=zo (ISO 8601)
  year: number // ISO-jaar van de week waar deze dag onder valt
  week: number // ISO-week van deze dag (groepering in de UI)
  werkbonNumbers: string[]
  uren: number
  overuren: number
  reisKm: number
  reisMin: number
}

/**
 * Response van GET /uren/me/periode. `weekStart`/`weekEnd` zijn de WERKELIJK
 * gedekte span: bij modus KALENDER gelijk aan [van, totEnMet], bij WEKEN de
 * maandag van de eerste week t/m de zondag van de laatste week (die kan dus
 * buiten de gevraagde periode vallen, consistent met de weekstaat).
 */
export interface UrenPeriodeDTO {
  van: string // gevraagde begindatum (echo), ISO yyyy-MM-dd
  totEnMet: string // gevraagde einddatum (echo), ISO yyyy-MM-dd
  /** FO-491 — de toegepaste optel-modus (echo). */
  modus: UrenPeriodeModus
  weekStart: string // eerste gedekte dag (zie hierboven)
  weekEnd: string // laatste gedekte dag (zie hierboven)
  totalen: UrenPeriodeTotalenDTO
  weken: UrenPeriodeWeekDTO[] // chronologisch (oudste eerst)
  dagen: UrenPeriodeDagDTO[] // activiteit-dagen binnen de gedekte span, chronologisch
}

// ─── Zod request schemas ─────────────────────────────────────────────────────

export const submitWeekstaatSchema = z.object({
  year: z.number().int().min(2000).max(2100),
  week: z.number().int().min(1).max(53),
})
export type SubmitWeekstaatInput = z.infer<typeof submitWeekstaatSchema>

export const reviewWeekstaatSchema = z
  .object({
    decision: z.enum(['GOEDGEKEURD', 'AFGEKEURD']),
    note:     z.string().trim().max(2000).optional(),
  })
  .refine(
    (val) => val.decision !== 'AFGEKEURD' || (val.note != null && val.note.length > 0),
    { message: 'Bij afkeur is een toelichting verplicht', path: ['note'] },
  )
export type ReviewWeekstaatInput = z.infer<typeof reviewWeekstaatSchema>

export const withdrawWeekstaatSchema = z.object({
  note: z.string().trim().max(2000).optional(),
})
export type WithdrawWeekstaatInput = z.infer<typeof withdrawWeekstaatSchema>

/**
 * Filter-query voor GET /uren/weekstaten.
 *
 * Periode-filter (vanaf/totEnMet) maakt over-week-aggregatie mogelijk
 * voor maand-/kwartaal-export — onafhankelijk van year/week-exact.
 * Beide filter-paren mogen gecombineerd worden of los gebruikt.
 */
export const listWeekstaatQuerySchema = z.object({
  status:     z.enum(WEEKSTAAT_STATUS).optional(),
  employeeId: idParam().optional(),
  // payroll-werkruimte 18→8 — server-side BV-/afdeling-filter (via de
  // employee-relatie), zodat de capped fetch nooit client-side gefilterd wordt.
  entityId:     idParam().optional(),
  departmentId: idParam().optional(),
  year:       z.coerce.number().int().min(2000).max(2100).optional(),
  week:       z.coerce.number().int().min(1).max(53).optional(),
  vanaf:      z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),  // ISO yyyy-MM-dd
  totEnMet:   z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  page:       z.coerce.number().int().positive().default(1),
  // Backoffice haalt de volledige (gefilterde) set in één keer op zodat sorteren
  // én pagineren client-side gebeuren (globale kolom-sortering i.p.v. per
  // server-pagina). De cap blijft een vangnet tegen ongebreidelde fetches.
  pageSize:   z.coerce.number().int().positive().max(2000).default(20),
})
export type ListWeekstaatQueryInput = z.infer<typeof listWeekstaatQuerySchema>

/**
 * FO-115 (batch-22) — query voor GET /uren/me/periode (eigen-scope portal).
 *
 * Concrete grenzen (yyyy-mm-dd, inclusief), begindatum <= einddatum. Gedeeld door
 * de API (`safeParse` -> `sendValidationError`) én het portal-form (client-side
 * validatie vóór verzenden), zodat het form nooit iets stuurt dat de API weigert.
 * Géén `employeeId` in de query: de eigen-scope wordt server-side afgedwongen
 * (`findOwnEmployeeId`), nooit uit de query.
 *
 * FO-491 (batch-66) — `modus` bepaalt hoe de periode optelt (zie
 * `UREN_PERIODE_MODI`). Weggelaten = KALENDER: de portal toont standaard de
 * kalenderperiode; de hele-weken-variant vraagt hij bewust aan met `WEKEN`.
 */
/** Bestaat de yyyy-mm-dd echt in de kalender? (de regex laat bv. 2026-02-30 door.) */
function isGeldigeKalenderdatum(s: string): boolean {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s)
  if (!m) return false
  const jaar = Number(m[1])
  const maand = Number(m[2])
  const dag = Number(m[3])
  const d = new Date(Date.UTC(jaar, maand - 1, dag))
  return d.getUTCFullYear() === jaar && d.getUTCMonth() === maand - 1 && d.getUTCDate() === dag
}

/** Max. periode-lengte (dagen) — weert een te brede sweep over de uren-bron. */
export const UREN_PERIODE_MAX_DAGEN = 366

export const urenPeriodeQuerySchema = z
  .object({
    van: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een geldige begindatum.')
      .refine(isGeldigeKalenderdatum, 'Kies een geldige begindatum.'),
    totEnMet: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Kies een geldige einddatum.')
      .refine(isGeldigeKalenderdatum, 'Kies een geldige einddatum.'),
    modus: z.enum(UREN_PERIODE_MODI, 'Kies een geldige weergave.').default('KALENDER'),
  })
  .refine((v) => v.van <= v.totEnMet, {
    message: 'De begindatum mag niet na de einddatum liggen.',
    path: ['totEnMet'],
  })
  // Span-grens in het GEDEELDE schema (niet alleen server-side), zodat een te
  // lange eigen periode al client-side inline wordt afgekeurd — geen round-trip,
  // geen dubbele melding. Bij begin > eind pakt de vorige refine het al.
  .refine(
    (v) => {
      const dagen = Math.round(
        (Date.parse(`${v.totEnMet}T00:00:00.000Z`) - Date.parse(`${v.van}T00:00:00.000Z`)) /
          86_400_000,
      )
      return Number.isNaN(dagen) || dagen < 0 || dagen <= UREN_PERIODE_MAX_DAGEN
    },
    {
      message: 'De periode mag maximaal één jaar beslaan.',
      path: ['totEnMet'],
    },
  )
export type UrenPeriodeQueryInput = z.infer<typeof urenPeriodeQuerySchema>

// ─── FO-324 P2 — weekstaat-cumulatieven (vrij bereik, uren-invalshoek) ────────

/** Eén betrokken week binnen het cumulatief-bereik (uitklap + doorklik). */
export interface UrenCumulatiefWeekDTO {
  year: number
  week: number
  /** null als er (nog) geen weekstaat-rij is voor deze week. */
  weekstaatId: string | null
  /** WeekstaatStatus, of 'GEEN' als er geen weekstaat-rij is. */
  status: WeekstaatStatus | 'GEEN'
  gewerkteUren: number
  reisUren: number
  overuren: number
  minuren: number
  verlofUren: number
  atvUren: number
  ziekteUren: number
  feestdagUren: number
  onbetaaldUren: number
  /** FO-352 — som van alle toeslag-uren van deze week (= som van `toeslagPerSoort`). */
  toeslagUren: number
  /** FO-352 — toeslag-uren per soort (uitklap + tooltip). */
  toeslagPerSoort: UrenCumulatiefToeslagDTO
  /** Norm voor de in-bereik-dagen van deze week (pro-rata bij dag-snijding). */
  weeknorm: number
}

/** Cumulatieve uren per medewerker over het gekozen bereik (dag-gesneden). */
export interface UrenCumulatiefRowDTO {
  employeeId: string
  employeeNumber: string
  naam: string
  gewerkteUren: number
  reisUren: number
  overuren: number
  minuren: number
  verlofUren: number
  atvUren: number
  ziekteUren: number
  feestdagUren: number
  onbetaaldUren: number
  /** FO-352 — som van alle toeslag-uren over het bereik. */
  toeslagUren: number
  /** FO-352 — toeslag-uren per soort over het bereik. */
  toeslagPerSoort: UrenCumulatiefToeslagDTO
  meetellendeAfwezigeUren: number
  totaalInclusiefAfwezigheid: number
  weeknorm: number
  saldo: number
  weken: UrenCumulatiefWeekDTO[]
  /** Aantal betrokken weken zonder weekstaat-rij (maar met data). */
  ontbrekendeWeken: number
}

/** Totalenrij over alle medewerkers in de selectie. */
export interface UrenCumulatiefTotalenDTO {
  gewerkteUren: number
  reisUren: number
  overuren: number
  minuren: number
  verlofUren: number
  atvUren: number
  ziekteUren: number
  feestdagUren: number
  onbetaaldUren: number
  /** FO-352 — som van alle toeslag-uren over de selectie. */
  toeslagUren: number
  meetellendeAfwezigeUren: number
  totaalInclusiefAfwezigheid: number
  weeknorm: number
  saldo: number
}

export interface UrenCumulatiefResponseDTO {
  van: string
  totEnMet: string
  label: string
  rows: UrenCumulatiefRowDTO[]
  totalen: UrenCumulatiefTotalenDTO
}

export const urenCumulatiefQuerySchema = z
  .object({
    van: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (yyyy-mm-dd)')
      .refine(isGeldigeKalenderdatum, 'Kies een geldige begindatum.'),
    totEnMet: z
      .string()
      .regex(/^\d{4}-\d{2}-\d{2}$/, 'Ongeldige datum (yyyy-mm-dd)')
      .refine(isGeldigeKalenderdatum, 'Kies een geldige einddatum.'),
    /** Optioneel: scope op één medewerker (picker). */
    employeeId: z.string().min(1).optional(),
    /** Optioneel: scope op een BV (entiteit). */
    entityId: z.string().min(1).optional(),
    /** Optioneel: scope op een afdeling. */
    departmentId: z.string().min(1).optional(),
  })
  .refine((v) => v.van <= v.totEnMet, {
    message: 'Begindatum mag niet na de einddatum liggen.',
    path: ['totEnMet'],
  })
  .refine(
    (v) => {
      // Max 12 maanden (zelfde grens als het periode-overzicht).
      const dagen = Math.round(
        (Date.parse(`${v.totEnMet}T00:00:00.000Z`) - Date.parse(`${v.van}T00:00:00.000Z`)) /
          86_400_000,
      )
      return Number.isNaN(dagen) || dagen < 0 || dagen <= UREN_PERIODE_MAX_DAGEN
    },
    {
      message: 'Het bereik mag maximaal twaalf maanden beslaan.',
      path: ['totEnMet'],
    },
  )
export type UrenCumulatiefQueryInput = z.infer<typeof urenCumulatiefQuerySchema>

// ── FO-352 (batch-40) — derde uitklapniveau: week → dagregels ────────────────

/**
 * Query voor `GET /uren/cumulatief/dagregels`. LAZY: het scherm laadt dit pas
 * bij het uitklappen van één week (T10 — een 12-maands-bereik over 50
 * medewerkers mag het scherm niet verdrinken).
 */
export const urenCumulatiefDagregelsQuerySchema = z.object({
  employeeId: z.string().min(1),
  year: z.coerce.number().int().min(2000).max(2100),
  week: z.coerce.number().int().min(1).max(53),
})
export type UrenCumulatiefDagregelsQueryInput = z.infer<
  typeof urenCumulatiefDagregelsQuerySchema
>

/**
 * Eén dagregel onder een week: de brug van een cumulatief getal naar de bon.
 *
 * Bewust GEEN euro's (A3 / §6): dit scherm hangt onder `UREN.canView`; tarieven
 * en kostprijzen horen op de payroll-schermen met hun eigen gate.
 */
export interface UrenCumulatiefDagregelDTO {
  /** yyyy-MM-dd */
  datum: string
  werkbonId: string | null
  werkbonNumber: string | null
  klantNaam: string | null
  uren: number
  /**
   * WEERGAVE: uitbetaalde reistijd van deze dagregel (reguliere bon: uit de
   * ReisUrenBerekening; consignatie: de
   * dag-entry). Som over de week = de weekregel. NIET de correctie-basis.
   */
  reisUren: number
  /** WEERGAVE: reis-kilometers van deze dagregel (zelfde bron als `reisUren`). */
  reisKm: number
  /**
   * CORRECTIE-BASIS: de reisminuten die de correctie-API als origineel vastlegt
   * (`correctieOrigineelReis(dayEntry)`). De correctie-dialog vult hiermee voor,
   * zodat "alleen uren corrigeren" een reis-delta van 0 geeft. Wijkt af van
   * `reisUren` zodra de reis uit de reisuren-berekening komt.
   */
  correctieBasisReisMin: number
  /** CORRECTIE-BASIS: reis-km die de correctie-API als origineel vastlegt. */
  correctieBasisReisKm: number
  overuren: number
  /** Som van de toeslag-uren op deze dag. */
  toeslagUren: number
  /** Toeslag-uren per soort op deze dag (tooltip). */
  toeslagPerSoort: UrenCumulatiefToeslagDTO
  /** Leesbaar ploegensysteem ("2-ploegen"), of null. */
  dienstTypeLabel: string | null
  /**
   * §8.1 — een handmatige weekstaat-post heeft geen werkbon: corrigeren gaat via
   * weekstaat-heropenen, niet via `PayrollCorrection`.
   */
  handmatigePost: boolean
  /** FO-352 §3.3 — id van de dag-entry, om een correctie op voor te vullen. */
  dayEntryId: string | null
  /** Aantal openstaande (OPEN) correcties op deze dagregel → badge. */
  openstaandeCorrecties: number
  /** True wanneer de payroll-maand vergrendeld is (correctie-knop toont de lock-reden). */
  maandVergrendeld: boolean
  /**
   * Alleen reis op deze dag: de nachtdienst die hier start telt met zijn uren op
   * de einddag (PO 2026-10-16), de rit blijft op deze dag. Geen correctie-ingang.
   */
  alleenReis: boolean
}

export interface UrenCumulatiefDagregelsResponseDTO {
  employeeId: string
  year: number
  week: number
  dagregels: UrenCumulatiefDagregelDTO[]
}
